import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { ProjetosElegiveisPicker } from "@/components/finance/ProjetosElegiveisPicker";
import { useAgencias } from "@/hooks/use-agencias";
import { useCriarProposta, useProjetosElegiveis } from "@/hooks/use-finance-propostas";
import { usePageNavigation } from "@/hooks/use-page-navigation";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";
import { formatCents, Moeda, parseMoeda } from "@/utils/money";

const PARAMETRO_AGENCIA = "agencia";

/** Escolhe a agência e os períodos e cria a proposta em rascunho. A agência fica na URL, então dá para mandar o link. */
export default function NovaProposta() {
  const navigate = useNavigate();
  const { navigateBack } = usePageNavigation();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const agenciaId = searchParams.get(PARAMETRO_AGENCIA) ?? "";
  const { data: agencias = [] } = useAgencias();
  const elegiveis = useProjetosElegiveis(agenciaId || null);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const criar = useCriarProposta();

  const periodosSelecionados = (elegiveis.data?.items ?? []).filter((item) =>
    selecionados.includes(item.controle_projeto_id),
  );
  const moedasSelecionadas = [...new Set(periodosSelecionados.map((item) => parseMoeda(item.moeda)))];
  const somaNaMoeda = (moeda: Moeda) =>
    periodosSelecionados
      .filter((item) => parseMoeda(item.moeda) === moeda)
      .reduce((total, item) => total + (item.valor_profissional_cents ?? 0), 0);
  const moedasMisturadas = moedasSelecionadas.length > 1;

  const trocarAgencia = (valor: string) => {
    setSearchParams(valor ? { [PARAMETRO_AGENCIA]: valor } : {}, { replace: true });
    setSelecionados([]);
  };

  const alternar = (controleProjetoId: string) => {
    setSelecionados((atual) =>
      atual.includes(controleProjetoId)
        ? atual.filter((id) => id !== controleProjetoId)
        : [...atual, controleProjetoId],
    );
  };

  const handleCriar = async () => {
    if (!agenciaId || selecionados.length === 0) return;
    try {
      const proposta = await criar.mutateAsync({ agencia_id: agenciaId, controle_projeto_ids: selecionados });
      toast({ title: "Proposta criada" });
      navigate(`/financeiro/propostas/${proposta.id}`, { replace: true });
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start gap-3">
        <Button variant="ghost" size="icon" aria-label="Voltar" onClick={() => navigateBack("/financeiro")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1 space-y-1">
          <h1 className="text-xl font-semibold">Nova proposta</h1>
          <p className="text-sm text-muted-foreground">Escolha a agência e os períodos que vão entrar na proposta.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {selecionados.length} {selecionados.length === 1 ? "período selecionado" : "períodos selecionados"}
            {selecionados.length > 0 &&
              ` · ${moedasSelecionadas.map((moeda) => formatCents(somaNaMoeda(moeda), moeda)).join(" + ")} dos profissionais`}
            {moedasMisturadas && ". Uma proposta tem uma moeda só: faça uma para cada."}
          </span>
          <Button
            type="button"
            disabled={!agenciaId || selecionados.length === 0 || moedasMisturadas || criar.isPending}
            onClick={handleCriar}
            className="gap-1.5"
          >
            {criar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Criar proposta
          </Button>
        </div>
      </div>

      <div className="max-w-md space-y-1.5">
        <Label>Agência</Label>
        <Combobox
          options={agencias.map((agencia) => ({ value: agencia.id, label: agencia.nome }))}
          value={agenciaId}
          onValueChange={trocarAgencia}
          allowCustomValue={false}
          placeholder="Selecione a agência..."
          searchPlaceholder="Buscar agência..."
        />
      </div>

      {agenciaId ? (
        <ProjetosElegiveisPicker
          key={agenciaId}
          agenciaId={agenciaId}
          selectedIds={selecionados}
          onToggle={alternar}
        />
      ) : (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Escolha uma agência para ver os períodos elegíveis.
        </p>
      )}
    </div>
  );
}
